/**
 * PRD Review API service.
 *
 * Handles API calls for the PRD Review Agent feature, including:
 * - Session management (start, get, end)
 * - Chat message sending
 * - Manual PRD content updates
 */

import type {
  GetReviewSessionResponse,
  SendReviewMessageRequest,
  SendReviewMessageResponse,
  StartReviewSessionRequest,
  StartReviewSessionResponse,
  UpdatePRDContentRequest,
  UpdatePRDContentResponse,
} from '~/types';

import { apiRequest } from './client';
import { HTTP_METHODS } from './config';

/**
 * PRD Document response from PRD documents API.
 * Backend uses alias_generator=to_camel, so fields are camelCase in JSON.
 */
export interface PRDDocumentResponse {
  id: string;
  projectId: string;
  content: string;
  questionCount: number | null;
  answeredQuestionCount: number;
  completenessScore: number;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * End a review session.
 *
 * Soft-deletes the session. The PRD content is already saved
 * (auto-save on each edit).
 *
 * @param sessionId - Session UUID
 * @throws {ApiError} 404 - Session not found
 * @throws {ApiError} 401 - Unauthorized
 */
export async function endReviewSession(sessionId: string): Promise<void> {
  await apiRequest<void>(`/prd/review/${sessionId}`, {
    method: HTTP_METHODS.DELETE,
  });
}

/**
 * Get PRD document content for a project.
 *
 * Fetches the PRD document associated with the project.
 *
 * @param projectId - Project UUID
 * @returns Promise resolving to PRD document with content
 * @throws {ApiError} 404 - PRD not found for project
 * @throws {ApiError} 401 - Unauthorized
 */
export async function getPRDContent(projectId: string): Promise<PRDDocumentResponse> {
  const response = await apiRequest<PRDDocumentResponse>(`/prd/generate/document/${projectId}`);

  return response.data;
}

/**
 * Get review session details.
 *
 * @param sessionId - Session UUID
 * @returns Promise resolving to session details
 * @throws {ApiError} 404 - Session not found
 * @throws {ApiError} 410 - Session expired
 * @throws {ApiError} 401 - Unauthorized
 */
export async function getReviewSession(sessionId: string): Promise<GetReviewSessionResponse> {
  const response = await apiRequest<GetReviewSessionResponse>(`/prd/review/${sessionId}`);

  return response.data;
}

/**
 * Send a chat message to the review agent.
 *
 * This is an async endpoint - it returns immediately and the agent
 * processes the message in the background. Updates are sent via SSE.
 *
 * @param request - Message request with session_id and content
 * @returns Promise resolving to processing status
 * @throws {ApiError} 404 - Session not found
 * @throws {ApiError} 409 - Agent is currently editing (try again later)
 * @throws {ApiError} 410 - Session expired
 * @throws {ApiError} 401 - Unauthorized
 */
export async function sendReviewMessage(
  request: SendReviewMessageRequest,
): Promise<SendReviewMessageResponse> {
  const response = await apiRequest<SendReviewMessageResponse>(
    `/prd/review/${request.session_id}/chat`,
    {
      method: HTTP_METHODS.POST,
      body: JSON.stringify({ content: request.content }),
    },
  );

  return response.data;
}

/**
 * Start or resume a PRD review session.
 *
 * If an active session exists for the project, returns the existing session.
 * Otherwise, creates a new session.
 *
 * @param request - Request with project_id
 * @returns Promise resolving to session details
 * @throws {ApiError} 404 - PRD not found for project
 * @throws {ApiError} 401 - Unauthorized
 * @throws {ApiError} 500 - Server error
 */
export async function startReviewSession(
  request: StartReviewSessionRequest,
): Promise<StartReviewSessionResponse> {
  const response = await apiRequest<StartReviewSessionResponse>('/prd/review/start', {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(request),
  });

  return response.data;
}

/**
 * Manually update PRD content.
 *
 * Used when user edits the PRD directly in the markdown editor.
 * Changes are debounced on the frontend (500ms recommended).
 *
 * @param request - Update request with session_id and content
 * @returns Promise resolving to update confirmation
 * @throws {ApiError} 404 - Session not found
 * @throws {ApiError} 409 - Agent is currently editing (try again later)
 * @throws {ApiError} 410 - Session expired
 * @throws {ApiError} 401 - Unauthorized
 */
export async function updatePRDContent(
  request: UpdatePRDContentRequest,
): Promise<UpdatePRDContentResponse> {
  const response = await apiRequest<UpdatePRDContentResponse>(
    `/prd/review/${request.session_id}/manual-edit`,
    {
      method: HTTP_METHODS.POST,
      body: JSON.stringify({ content: request.content }),
    },
  );

  return response.data;
}
